import { Component } from '@angular/core';
import { TSIconModule } from 'tsui/lib/ts-icon/ts-icon.module';
import { Dialog } from '@angular/cdk/dialog';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { FoldersTabService } from '@shared/modules/folders/folders.service';
import { FoldersTabViewFileComponent } from '@shared/modules/folders/view-file/view-file.component';
import { AccountService } from 'app/core/services/account.service';
import { AccountFoldersService } from 'app/modules/folders/folders.service';
import { FileResource, Document as FileDocument } from 'app/shared/modules/folders/folders.types';

type FileItem = { id: number; label: string };

@Component({
  standalone: true,
  imports: [TSIconModule],
  selector: 'app-table-file-resource-renderer',
  template: `
    <div class="cell">
      <div class="flex flex-row">
        @for (item of items; track item.id; let last = $last) {
          <div class="flex flex-row items-center mr-1">
            <span class="file-label mr-[2px]">{{ item.label }}</span>
            <button type="button" class="icon-btn" (click)="open(item)" title="Open file">
              <ts-icon icon="ts_eye"></ts-icon>
            </button>
            {{ last ? '' : ', ' }}
          </div>
        }
      </div>
    </div>
  `,
  styles: [
    `
      .cell {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
      }
      .labels {
        flex: 1 1 auto;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .icon-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 20px;
        height: 20px;
        padding: 0;
        border: none;
        background: transparent;
        cursor: pointer;
      }
      .icon-btn:focus {
        outline: none;
      }
    `,
  ],
})
export class TableFileResourceRendererComponent implements ICellRendererAngularComp {
  params: ICellRendererParams;
  items: FileItem[] = [];

  constructor(
    private readonly dialog: Dialog,
    private readonly folders: FoldersTabService,
    private readonly accountFoldersService: AccountFoldersService,
    private readonly accountService: AccountService,
  ) {
    const accountId = this.accountService.getCurrentAccount().id;
    this.folders.accountId = accountId;
    this.accountFoldersService.accountId = accountId;
  }

  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.items = this.parseValue(params.value);
  }

  refresh(params: ICellRendererParams): boolean {
    this.items = this.parseValue(params.value);
    return true;
  }

  open(item: FileItem): void {
    this.folders.getFileById(item.id).subscribe((fileResource) => {
      if (!fileResource) return;
      this.dialog.open(
        FoldersTabViewFileComponent,
        FoldersTabViewFileComponent.getDialogConfig({
          fileResource,
          accountScoped: true,
          onDeleteAction: (document: FileDocument, fileDeleted?: boolean) =>
            this.onDeleteAction(fileResource, document, item, fileDeleted),
        }),
      );
    });
  }

  // Called after a document version has been deleted in the viewer.
  // The viewer already deletes the file resource itself when the last version is removed,
  // so here we only need to keep the grid cell in sync.
  onDeleteAction = (file: FileResource, _document: FileDocument, item: FileItem, fileDeleted?: boolean): void => {
    if (!file?.id || !fileDeleted) return;
    this.removeTokenFromCell(item.id);
  };

  private removeTokenFromCell(fileId: number): void {
    const field = this.params?.colDef?.field as string;
    if (!field) return;

    // Always read the latest value from the node data to avoid using a stale params.value
    const currentValue = this.params?.node?.data?.[field] ?? this.params.value;
    const existingTokens = this.parseExistingTokens(currentValue);
    const filtered = existingTokens.filter((token) => {
      const pipeIndex = token.indexOf('|');
      const id = Number(token.substring(0, pipeIndex));
      return id !== fileId;
    });

    // Write back in the same shape as original (array vs single string)
    let newValue: any = null;
    if (Array.isArray(currentValue)) {
      newValue = filtered;
    } else if (typeof currentValue === 'string') {
      newValue = filtered[0] ?? null;
    }

    // Update row data and local rendered items
    this.params.node.setDataValue(field, newValue);
    // Keep params.value in sync to prevent future operations from using stale value
    this.params.value = newValue;
    this.items = this.parseValue(newValue);
    this.params.api?.refreshCells({ rowNodes: [this.params.node], columns: [this.params.column?.getColId?.()] });
  }

  private parseValue(value: any): FileItem[] {
    if (!value) return [];
    const toItem = (v: string): FileItem => {
      const pipeIndex = v.indexOf('|');
      const id = Number(v.substring(0, pipeIndex));
      const label = v.substring(pipeIndex + 1);
      return { id, label };
    };

    if (Array.isArray(value)) {
      return (value as string[]).map(toItem);
    }
    if (typeof value === 'string' && value.includes('|')) {
      return [toItem(value)];
    }
    return [];
  }

  private parseExistingTokens(value: any): string[] {
    if (!value) return [];
    if (Array.isArray(value)) {
      return (value as string[]).filter((v) => typeof v === 'string' && v.includes('|'));
    }
    if (typeof value === 'string' && value.includes('|')) {
      return [value];
    }
    return [];
  }
}
